import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IoMdArrowBack } from "react-icons/io";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  ATTENDANCE_STATUS_OPTIONS,
  RESULT_STATUS_OPTIONS,
  buildEmployeeSelectOptions,
  buildSimpleOptions,
  getApiErrorMessage,
} from "../hrRecordsHelpers";
import HrUploadControl from "../HrUploadControl";
import "../../forms/UserForm.css";
import "../hrRecords.css";

const parseApiDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toApiDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function TrainingParticipantFormPage({ isSidebarExpanded, mode = "create" }) {
  const navigate = useNavigate();
  const { sessionId, participantId } = useParams();
  const isEdit = mode === "edit";
  const [formData, setFormData] = useState({
    session_id: "",
    actual_man_power_id: "",
    attendance_status: "present",
    result_status: "pass",
    score: "",
    certificate_number: "",
    issue_date: null,
    expiry_date: null,
    next_due_date: null,
    remarks: "",
  });
  const [sessions, setSessions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setPageLoading(true);
        const [sessionsResponse, employeesResponse] = await Promise.all([
          fetchAllSessions(),
          hrRecordsApi.fetchEmployeesDropdown(),
        ]);

        setSessions(buildSimpleOptions(sessionsResponse, "id", "session_code"));
        setEmployees(buildEmployeeSelectOptions(employeesResponse.data || []));

        if (isEdit && sessionId && participantId) {
          const response = await hrRecordsApi.getTrainingSession(sessionId);
          const participant = (response.participants || []).find(
            (item) => String(item.participant_id) === String(participantId),
          );
          if (participant) {
            setFormData({
              session_id: sessionId,
              actual_man_power_id: String(participant.actual_man_power_id || ""),
              attendance_status: participant.attendance_status || "present",
              result_status: participant.result_status || "pass",
              score: participant.score || "",
              certificate_number: participant.certificate_number || "",
              issue_date: parseApiDate(participant.issue_date),
              expiry_date: parseApiDate(participant.expiry_date),
              next_due_date: parseApiDate(participant.next_due_date),
              remarks: participant.remarks || "",
            });
          }
        } else if (sessionId) {
          setFormData((prev) => ({ ...prev, session_id: sessionId }));
        }
      } catch (error) {
        console.error("Error loading participant form:", error);
        setSeverity("error");
        setSnackbarMessage("Failed to load participant data");
        setSnackbarOpen(true);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [isEdit, participantId, sessionId]);

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.session_id) {
      validationErrors.session_id = "Session is required.";
    }
    if (!formData.actual_man_power_id) {
      validationErrors.actual_man_power_id = "Employee is required.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;

    const payload = {
      actual_man_power_id: formData.actual_man_power_id,
      attendance_status: formData.attendance_status,
      result_status: formData.result_status,
      score: formData.score || undefined,
      certificate_number: formData.certificate_number || undefined,
      issue_date: toApiDate(formData.issue_date) || undefined,
      expiry_date: toApiDate(formData.expiry_date) || undefined,
      next_due_date: toApiDate(formData.next_due_date) || undefined,
      remarks: formData.remarks || undefined,
    };

    try {
      setLoading(true);
      let finalParticipantId = participantId;

      if (isEdit) {
        await hrRecordsApi.updateTrainingParticipant(participantId, payload);
      } else {
        const response = await hrRecordsApi.saveTrainingParticipants(formData.session_id, {
          participants: [payload],
        });
        finalParticipantId = response.participants?.[0]?.participant_id;
      }

      if (selectedFiles.length && finalParticipantId) {
        await hrRecordsApi.uploadParticipantCertificate(finalParticipantId, selectedFiles);
      }

      setSeverity("success");
      setSnackbarMessage(
        `Participant ${isEdit ? "updated" : "added"} successfully`,
      );
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate("/hr-training-participants");
      }, 1000);
    } catch (error) {
      console.error("Error saving participant:", error);
      setSeverity("error");
      setSnackbarMessage(
        getApiErrorMessage(error, "Failed to save participant"),
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    setErrors((prevState) => ({ ...prevState, [name]: "" }));
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/hr-training-participants")}
                  />
                </Tooltip>
                {isEdit ? "Edit Training Participant" : "New Training Participant"}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="new-data-form hr-records-form-grid-two">
                  <div className="form-group-select">
                    <label htmlFor="session_id" className="hr_training_labels">
                      Session<span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="session_id"
                        className="hr-records-select"
                        options={sessions}
                        value={sessions.find((item) => item.value === formData.session_id) || null}
                        onChange={(option) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            session_id: option?.value || "",
                          }))
                        }
                        isSearchable
                        isDisabled={isEdit}
                      />
                    </div>
                    {errors.session_id && (
                      <div className="error-message">{errors.session_id}</div>
                    )}
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="actual_man_power_id" className="hr_training_labels">
                      Employee<span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="actual_man_power_id"
                        className="hr-records-select"
                        options={employees}
                        value={
                          employees.find(
                            (item) =>
                              String(item.value) ===
                              String(formData.actual_man_power_id),
                          ) || null
                        }
                        onChange={(option) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            actual_man_power_id: option?.value || "",
                          }))
                        }
                        isSearchable
                        isDisabled={isEdit}
                      />
                    </div>
                    {errors.actual_man_power_id && (
                      <div className="error-message">
                        {errors.actual_man_power_id}
                      </div>
                    )}
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="attendance_status" className="hr_training_labels">Attendance Status</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="attendance_status"
                        className="hr-records-select"
                        options={ATTENDANCE_STATUS_OPTIONS}
                        value={
                          ATTENDANCE_STATUS_OPTIONS.find(
                            (item) => item.value === formData.attendance_status,
                          ) || null
                        }
                        onChange={(option) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            attendance_status: option?.value || "",
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label htmlFor="result_status" className="hr_training_labels">Result Status</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        id="result_status"
                        className="hr-records-select"
                        options={RESULT_STATUS_OPTIONS}
                        value={
                          RESULT_STATUS_OPTIONS.find(
                            (item) => item.value === formData.result_status,
                          ) || null
                        }
                        onChange={(option) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            result_status: option?.value || "",
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="score" className="hr_training_labels">Score</label>
                    <div className="input-group">
                      <input
                        type="number"
                        name="score"
                        value={formData.score}
                        onChange={handleInputChange}
                        placeholder="Enter score"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="certificate_number" className="hr_training_labels">Certificate Number</label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="certificate_number"
                        value={formData.certificate_number}
                        onChange={handleInputChange}
                        placeholder="Enter certificate number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="issue_date" className="hr_training_labels">Issue Date</label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.issue_date}
                        onChange={(date) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            issue_date: date,
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select issue date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="expiry_date" className="hr_training_labels">Expiry Date</label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.expiry_date}
                        onChange={(date) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            expiry_date: date,
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select expiry date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="next_due_date" className="hr_training_labels">Next Due Date</label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.next_due_date}
                        onChange={(date) =>
                          setFormData((prevState) => ({
                            ...prevState,
                            next_due_date: date,
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select due date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="remarks" className="hr_training_labels">Remarks</label>
                    <div className="input-group">
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleInputChange}
                        placeholder="Enter remarks"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="certificate_upload" className="hr_training_labels">Certificate Upload</label>
                    <div className="input-group hr-records-form-control">
                      <HrUploadControl
                        buttonText="Upload File"
                        popupTitle="Certificate Attachments"
                        newFiles={selectedFiles}
                        onFilesSelected={(files) =>
                          setSelectedFiles((prevState) => [...prevState, ...files])
                        }
                        onValidationError={(message) => {
                          setSeverity("error");
                          setSnackbarMessage(message);
                          setSnackbarOpen(true);
                        }}
                        onRemoveNewFile={(fileIndex) =>
                          setSelectedFiles((prevState) =>
                            prevState.filter((_, current) => current !== fileIndex),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className={isEdit ? "data-edit-form-save-button" : "data-form-save-button"}
                type="button"
                onClick={handleSaveClick}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className={
                  isEdit ? "data-edit-form-cancel-button" : "data-form-cancel-button"
                }
                onClick={() => navigate("/hr-training-participants")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

async function fetchAllSessions() {
  const first = await hrRecordsApi.listTrainingSessions({ page: 1, per_page: 200 });
  const total = first.total_items || 0;
  const pages = Math.max(1, Math.ceil(total / 200));
  const pageRequests = [];
  for (let page = 2; page <= pages; page += 1) {
    pageRequests.push(hrRecordsApi.listTrainingSessions({ page, per_page: 200 }));
  }
  const results = await Promise.all(pageRequests);
  return [
    ...(first.items || []),
    ...results.flatMap((result) => result.items || []),
  ];
}

export default TrainingParticipantFormPage;
